import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldOff, ShieldCheck, Lock, LockOpen, ExternalLink } from "lucide-react";

/** Brand */
const BG = "#0C0C1A";
const CARD = "#141428";
const BORDER = "#2A2A40";

/** API base */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/** UI bits */
const Badge = ({ children, tone = "default" }) => {
  const map = {
    default: "bg-zinc-800/60 text-zinc-200 border-zinc-700/50",
    green: "bg-emerald-600/15 text-emerald-300 border-emerald-700/40",
    red: "bg-rose-600/15 text-rose-300 border-rose-700/40",
    amber: "bg-amber-600/15 text-amber-300 border-amber-700/40",
    pink: "bg-pink-600/15 text-pink-300 border-pink-700/40",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs border whitespace-nowrap ${map[tone] || map.default}`}>
      {children}
    </span>
  );
};

const Section = ({ title, right, children }) => (
  <div className="rounded-xl p-4 mb-6" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
      <div className="text-zinc-100 font-medium">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

/** Small pager */
function Pager({ page, pageSize, total, onPage }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-3 text-xs text-zinc-400">
      <div>
        Page <span className="text-zinc-200">{page}</span> of{" "}
        <span className="text-zinc-200">{totalPages}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="px-3 py-1.5 rounded-md bg-zinc-900/70 text-zinc-100 disabled:opacity-50 border border-zinc-700/50"
        >
          Prev
        </button>
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="px-3 py-1.5 rounded-md bg-zinc-900/70 text-zinc-100 disabled:opacity-50 border border-zinc-700/50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

/** Utility: pick best thumbnail URL */
const getThumbUrl = (a) =>
  a?.thumb_url ||
  a?.meta?.thumb_url ||
  a?.meta?.thumbnail_url ||
  a?.media_url ||
  "";

/** Tiny preview */
const Thumb = ({ url, kind }) => {
  if (!url) {
    return (
      <div
        className="w-12 h-12 rounded-md bg-zinc-800/80 flex items-center justify-center text-[10px] text-zinc-400 select-none"
        title={kind || "asset"}
      >
        —
      </div>
    );
  }
  const isVideo = String(kind).toLowerCase() === "video";
  return (
    <div className="relative">
      <img
        src={url}
        loading="lazy"
        alt="Preview"
        className="w-12 h-12 rounded-md object-cover bg-zinc-800"
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const placeholder = e.currentTarget.nextSibling;
          if (placeholder) placeholder.style.display = "flex";
        }}
      />
      <div
        style={{ display: "none" }}
        className="w-12 h-12 rounded-md bg-zinc-800/80 flex items-center justify-center text-[10px] text-zinc-400 select-none"
        title={kind || "asset"}
      >
        {isVideo ? "VIDEO" : "—"}
      </div>
      {isVideo ? (
        <span className="absolute -bottom-1 -right-1 text-[9px] px-1 py-0.5 rounded bg-zinc-900/90 border border-zinc-700/60 text-zinc-300">
          vid
        </span>
      ) : null}
    </div>
  );
};

/** sub status → tone */
const statusTone = (s) => {
  if (!s) return "default";
  const v = String(s).toLowerCase();
  if (v === "active" || v === "trialing") return "green";
  if (v === "past_due" || v === "unpaid") return "amber";
  if (v === "canceled" || v === "incomplete_expired") return "red";
  return "default";
};

/** Parse ISO strings or epoch seconds into Date */
function parseTs(ts) {
  if (!ts && ts !== 0) return null;
  if (typeof ts === "number") {
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return new Date(ms);
  }
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

const intervalLabel = (i) => (i === "year" ? "annual" : i === "month" ? "monthly" : null);

const nextPeriodText = (cancelAtEnd, ts, planInterval) => {
  const d = parseTs(ts);
  if (!d) return null;
  const when = d.toLocaleString();
  const il = intervalLabel(planInterval);
  return cancelAtEnd ? `Cancels on ${when}${il ? ` · ${il}` : ""}` : `Renews on ${when}${il ? ` · ${il}` : ""}`;
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [user, setUser] = useState(null);

  const [recentAssets, setRecentAssets] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);

  const [addons, setAddons] = useState([]); // raw add-on line items/purchases
  const [addonsCount, setAddonsCount] = useState(0); // total purchased
  const [addonCounts, setAddonCounts] = useState({
    addon_url_count: 0,
    addon_video_count: 0,
    addon_image_count: 0,
  }); // summarized purchased capacity

  // NEW: per-user revenue (EUR)
  const [userRevenue, setUserRevenue] = useState({ tiers_eur: 0, addons_eur: 0, total_eur: 0 });
  const euro = (n) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

  // pagination state (10 per page)
  const PAGE_SIZE = 10;
  const [assetsPage, setAssetsPage] = useState(1);
  const [projectsPage, setProjectsPage] = useState(1);
  const [addonsPage, setAddonsPage] = useState(1);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const token = await getToken();
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response from /api/admin/users/:id:", text.slice(0, 200));
        throw new Error("Unexpected non-JSON response from API.");
      }

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to load user");

      setUser(json.user || null);
      setRecentAssets(Array.isArray(json.recentAssets) ? json.recentAssets : []);
      setRecentProjects(Array.isArray(json.recentProjects) ? json.recentProjects : []);

      // Add-ons: accept either server-provided or default to empty
      setAddons(Array.isArray(json.addons) ? json.addons : []);
      setAddonsCount(Number(json.addons_count || 0));
      setAddonCounts({
        addon_url_count: Number(json?.addon_counts?.addon_url_count || 0),
        addon_video_count: Number(json?.addon_counts?.addon_video_count || 0),
        addon_image_count: Number(json?.addon_counts?.addon_image_count || 0),
      });

      // NEW: revenue
      setUserRevenue(json.revenue || { tiers_eur: 0, addons_eur: 0, total_eur: 0 });

      // reset pagers on new load
      setAssetsPage(1);
      setProjectsPage(1);
      setAddonsPage(1);
    } catch (e) {
      setErr(e.message || "Failed to load user");
    } finally {
      setLoading(false);
    }
  }, [getToken, id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const toggleUnlimited = async (enabled) => {
    try {
      const token = await getToken();
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}/unlimited`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ enabled }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to toggle unlimited");
      await fetchDetail();
    } catch (e) {
      setErr(e.message || "Failed to toggle unlimited");
    }
  };

  const toggleBlocked = async (blocked) => {
    try {
      const token = await getToken();
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(id)}/block`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ blocked }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to toggle block");
      await fetchDetail();
    } catch (e) {
      setErr(e.message || "Failed to toggle block");
    }
  };

  /** Fallback-resilient purchased counts */
  const purchased = useMemo(() => {
    const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    let urls = num(user?.addon_url_count) || num(addonCounts.addon_url_count);
    let videos = num(user?.addon_video_count) || num(addonCounts.addon_video_count);
    let images = num(user?.addon_image_count) || num(addonCounts.addon_image_count);

    if ((urls === 0 || videos === 0 || images === 0) && Array.isArray(addons) && addons.length > 0) {
      let sumUrls = 0, sumVideos = 0, sumImages = 0;
      for (const ad of addons) {
        const t = String(ad?.type || "").toLowerCase();
        const q = num(ad?.quantity || 1);
        if (t.includes("url")) sumUrls += q;
        else if (t.includes("video")) sumVideos += q;
        else if (t.includes("image") || t.includes("images")) sumImages += q;
      }
      if (urls === 0) urls = sumUrls;
      if (videos === 0) videos = sumVideos;
      if (images === 0) images = sumImages;
    }

    return { urls, videos, images };
  }, [user, addonCounts, addons]);

  // Pagination helpers
  const assetsTotal = recentAssets.length;
  const projectsTotal = recentProjects.length;
  const addonsTotal = addons.length;

  const assetsPageItems = useMemo(() => {
    const start = (assetsPage - 1) * PAGE_SIZE;
    return recentAssets.slice(start, start + PAGE_SIZE);
  }, [recentAssets, assetsPage]);

  const projectsPageItems = useMemo(() => {
    const start = (projectsPage - 1) * PAGE_SIZE;
    return recentProjects.slice(start, start + PAGE_SIZE);
  }, [recentProjects, projectsPage]);

  const addonsPageItems = useMemo(() => {
    const start = (addonsPage - 1) * PAGE_SIZE;
    return addons.slice(start, start + PAGE_SIZE);
  }, [addons, addonsPage]);

  const fmt = (ts) => {
    const d = parseTs(ts);
    return d ? d.toLocaleString() : "—";
  };

  /** determine billing inactive/cancelled display */
  const billingInactive =
    (!user?.current_tier && ["canceled", "incomplete_expired"].includes(String(user?.sub_status || "").toLowerCase())) ||
    (!user?.is_paying && !user?.current_tier);

  // NEW: never paid flag (strict) – zero revenue and not currently paying
  const neverPaid = (!user?.is_paying) && ((userRevenue?.total_eur || 0) === 0);

  /** ---- Mobile card renderers ---- */
  const AssetCard = ({ a }) => {
    const thumb = getThumbUrl(a);
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800/60" style={{ background: "rgba(255,255,255,0.02)" }}>
        <Thumb url={thumb} kind={a.kind} />
        <div className="min-w-0 flex-1">
          <div className="text-zinc-100 text-sm truncate">{a.kind || "asset"}</div>
          <div className="text-zinc-500 text-xs break-all">{a.id}</div>
          <div className="text-zinc-400 text-xs">{fmt(a.created_at)}</div>
        </div>
        {a.media_url ? (
          <a
            href={a.media_url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 text-xs"
          >
            <ExternalLink size={14} /> Open
          </a>
        ) : null}
      </div>
    );
  };

  const AddonCard = ({ ad }) => (
    <div className="p-3 rounded-lg border border-zinc-800/60" style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-zinc-100 text-sm">{ad.type || "add-on"}</div>
        <div className="text-zinc-300 text-sm">×{ad.quantity ?? 1}</div>
      </div>
      <div className="text-zinc-400 text-xs mt-1 break-all">{ad.description || "—"}</div>
      <div className="text-zinc-500 text-xs mt-1">{fmt(ad.created_at)}</div>
    </div>
  );

  const ProjectCard = ({ p }) => (
    <div className="p-3 rounded-lg border border-zinc-800/60" style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="text-zinc-100 text-sm truncate">{p.title || p.url || p.id}</div>
      <div className="text-zinc-500 text-xs mt-1">{fmt(p.created_at)}</div>
      {p.thumbnail_url ? (
        <div className="mt-2">
          <a
            href={p.thumbnail_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 text-xs"
          >
            <ExternalLink size={14} /> Open thumbnail
          </a>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: BG }}>
      {/* Sticky page header */}
      <div
        className="sticky top-0 z-10 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3"
        style={{ background: BG, borderBottom: `1px solid ${BORDER}` }}
      >
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
            aria-label="Back to Admin Users"
            title="Back to Admin Users"
          >
            <ArrowLeft size={16} />
            Back to list
          </Link>
          <h1 className="text-base sm:text-lg font-semibold text-zinc-100">Admin · User Detail</h1>
        </div>

        {/* Quick status at a glance */}
        {user ? (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1 -mx-1 px-1">
            {neverPaid ? (
              <Badge>Billing inactive</Badge>
            ) : billingInactive ? (
              <>
                <Badge tone="red">Cancelled</Badge>
                <Badge>Billing inactive</Badge>
              </>
            ) : (
              <>
                {user.is_paying ? <Badge tone="green">Paying</Badge> : <Badge>Not paying</Badge>}
                {user.current_tier ? <Badge tone="pink">{user.current_tier}</Badge> : <Badge>no tier</Badge>}
                <Badge tone={statusTone(user.sub_status)}>{user.sub_status || "no sub"}</Badge>
                {user.current_period_end ? (
                  <Badge tone={user.cancel_at_period_end ? "amber" : "green"}>
                    {nextPeriodText(user.cancel_at_period_end, user.current_period_end, user.plan_interval)}
                  </Badge>
                ) : null}
              </>
            )}

            {!neverPaid && user?.is_paying && user.last_resubscribed_at ? <Badge tone="green">Resubscribed</Badge> : null}
            {user?.has_unlimited ? <Badge tone="amber">Unlimited</Badge> : null}
            {user?.is_blocked ? <Badge tone="red">Blocked</Badge> : null}
          </div>
        ) : null}
      </div>

      <div className="px-4 py-4">
        {err ? <div className="mb-3 text-sm text-amber-300">{err}</div> : null}

        {/* Top summary card */}
        <div
          className="rounded-xl p-4 mb-6"
          style={{ background: CARD, border: `1px solid ${BORDER}` }}
        >
          {loading ? (
            <div className="text-sm text-zinc-400">Loading…</div>
          ) : user ? (
            <>
              {/* user header row */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#FC2783] to-[#9C27B0] flex items-center justify-center text-white font-bold text-xl select-none">
                    {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-zinc-100 text-lg font-semibold truncate">
                      {user.name || "—"}
                    </div>
                    <div className="text-zinc-400 text-sm break-all">{user.email}</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {user.has_unlimited ? (
                    <button
                      onClick={() => toggleUnlimited(false)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-600/40 text-amber-300 hover:border-amber-500"
                    >
                      <ShieldOff size={16} />
                      Remove Unlimited
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleUnlimited(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-600/40 text-emerald-300 hover:border-emerald-500"
                    >
                      <ShieldCheck size={16} />
                      Grant Unlimited
                    </button>
                  )}

                  {user.is_blocked ? (
                    <button
                      onClick={() => toggleBlocked(false)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-600/40 text-emerald-300 hover:border-emerald-500"
                    >
                      <LockOpen size={16} />
                      Unblock
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleBlocked(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-rose-600/40 text-rose-300 hover:border-rose-500"
                    >
                      <Lock size={16} />
                      Block
                    </button>
                  )}
                </div>
              </div>

              {/* Billing / flags */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Tier</div>
                  <div className="text-zinc-100">{user.current_tier || "—"}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Paying</div>
                  <div className="text-zinc-100">{user.is_paying ? "Yes" : "No"}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Sub status</div>
                  <div className="text-zinc-100">{user.sub_status || "—"}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">{user.cancel_at_period_end ? "Cancels" : "Renews"}</div>
                  <div className={`${user.cancel_at_period_end ? "text-amber-300" : "text-emerald-300"}`}>
                    {user.current_period_end
                      ? `${fmt(user.current_period_end)}${user.plan_interval ? ` · ${intervalLabel(user.plan_interval)}` : ""}`
                      : "—"}
                  </div>
                </div>
              </div>

              {/* Billing snapshot details (includes Stripe customer id) */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Stripe price id</div>
                  <div className="text-zinc-100 break-all">{user.stripe_price_id || "—"}</div>
                </div>

                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Plan interval</div>
                  <div className="text-zinc-100">
                    {user.plan_interval ? intervalLabel(user.plan_interval) || user.plan_interval : "—"}
                  </div>
                </div>

                {/* Stripe customer id */}
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Stripe customer id</div>
                  <div className="text-zinc-100 break-all flex items-center gap-2">
                    {user.stripe_customer_id || "—"}
                    {user?.stripe_customer_id ? (
                      <button
                        onClick={() => navigator.clipboard?.writeText(user.stripe_customer_id)}
                        className="px-2 py-0.5 text-xs rounded-md border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300 shrink-0"
                        title="Copy"
                      >
                        Copy
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* counters */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Images</div>
                  <div className="text-zinc-100">{user.images_count ?? 0}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Videos</div>
                  <div className="text-zinc-100">{user.videos_count ?? 0}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">URLs</div>
                  <div className="text-zinc-100">{user.urls_count ?? 0}</div>
                </div>
              </div>

              {/* timestamps */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Created</div>
                  <div className="text-zinc-100">{fmt(user.created_at)}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                  <div className="text-zinc-400">Last seen</div>
                  <div className="text-zinc-100">{fmt(user.last_seen_at)}</div>
                </div>
              </div>

              {/* Add-ons summary */}
              <div className="mt-4 rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                <div className="text-zinc-100 font-medium mb-2">Add-ons Summary</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">URLs purchased</div>
                    <div className="text-zinc-100 text-base font-medium">{purchased.urls}</div>
                  </div>
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">Videos purchased</div>
                    <div className="text-zinc-100 text-base font-medium">{purchased.videos}</div>
                  </div>
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">Images purchased</div>
                    <div className="text-zinc-100 text-base font-medium">{purchased.images}</div>
                  </div>
                </div>
              </div>

              {/* NEW: Revenue summary */}
              <div className="mt-4 rounded-lg p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${BORDER}` }}>
                <div className="text-zinc-100 font-medium mb-2">Revenue</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">Tiers</div>
                    <div className="text-zinc-100 text-base font-medium">{euro(userRevenue?.tiers_eur)}</div>
                  </div>
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">Add-ons</div>
                    <div className="text-zinc-100 text-base font-medium">{euro(userRevenue?.addons_eur)}</div>
                  </div>
                  <div className="rounded-md p-3 border border-zinc-800/60">
                    <div className="text-zinc-400">Total</div>
                    <div className="text-zinc-100 text-base font-medium">{euro(userRevenue?.total_eur)}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-400">User not found</div>
          )}
        </div>

        {/* Assets */}
        <Section title="Generated Assets">
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {assetsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No assets.</div>
            ) : (
              assetsPageItems.map((a) => <AssetCard key={a.id} a={a} />)
            )}
            {assetsPageItems.length > 0 ? (
              <Pager page={assetsPage} pageSize={PAGE_SIZE} total={assetsTotal} onPage={setAssetsPage} />
            ) : null}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            {assetsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No assets.</div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-zinc-800/60">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-300" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <tr>
                        <th className="text-left px-3 py-2">Preview</th>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Kind</th>
                        <th className="text-left px-3 py-2">Created</th>
                        <th className="text-right px-3 py-2">Open</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-200">
                      {assetsPageItems.map((a) => {
                        const thumb = getThumbUrl(a);
                        return (
                          <tr key={a.id} className="border-t border-zinc-800/60 align-middle">
                            <td className="px-3 py-2">
                              <Thumb url={thumb} kind={a.kind} />
                            </td>
                            <td className="px-3 py-2 text-zinc-400 break-all">{a.id}</td>
                            <td className="px-3 py-2">{a.kind}</td>
                            <td className="px-3 py-2 text-zinc-400">{fmt(a.created_at)}</td>
                            <td className="px-3 py-2 text-right">
                              <a
                                href={a.media_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300"
                              >
                                Open
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Pager page={assetsPage} pageSize={PAGE_SIZE} total={assetsTotal} onPage={setAssetsPage} />
              </>
            )}
          </div>
        </Section>

        {/* Add-ons purchased */}
        <Section title="Add-ons" right={<div className="text-zinc-400 text-sm">{addonsCount} purchased</div>}>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {addonsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No add-ons found for this user.</div>
            ) : (
              addonsPageItems.map((ad, i) => <AddonCard key={`${ad.session_id || ad.id || i}:${i}`} ad={ad} />)
            )}
            {addonsPageItems.length > 0 ? (
              <Pager page={addonsPage} pageSize={PAGE_SIZE} total={addonsTotal} onPage={setAddonsPage} />
            ) : null}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            {addonsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No add-ons found for this user.</div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-zinc-800/60">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-300" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <tr>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Description</th>
                        <th className="text-left px-3 py-2">Quantity</th>
                        <th className="text-left px-3 py-2">Created</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-200">
                      {addonsPageItems.map((ad, i) => (
                        <tr key={`${ad.session_id || ad.id || i}:${i}`} className="border-t border-zinc-800/60">
                          <td className="px-3 py-2">{ad.type || "add-on"}</td>
                          <td className="px-3 py-2 text-zinc-400 break-all">{ad.description || "—"}</td>
                          <td className="px-3 py-2">{ad.quantity ?? 1}</td>
                          <td className="px-3 py-2 text-zinc-400">{fmt(ad.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pager page={addonsPage} pageSize={PAGE_SIZE} total={addonsTotal} onPage={setAddonsPage} />
              </>
            )}
          </div>
        </Section>

        {/* Projects */}
        <Section title="Projects">
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {projectsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No projects.</div>
            ) : (
              projectsPageItems.map((p) => <ProjectCard key={p.id} p={p} />)
            )}
            {projectsPageItems.length > 0 ? (
              <Pager page={projectsPage} pageSize={PAGE_SIZE} total={projectsTotal} onPage={setProjectsPage} />
            ) : null}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            {projectsPageItems.length === 0 ? (
              <div className="text-sm text-zinc-400">No projects.</div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-zinc-800/60">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-300" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <tr>
                        <th className="text-left px-3 py-2">Title / URL</th>
                        <th className="text-left px-3 py-2">Created</th>
                        <th className="text-right px-3 py-2">Thumbnail</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-200">
                      {projectsPageItems.map((p) => (
                        <tr key={p.id} className="border-t border-zinc-800/60">
                          <td className="px-3 py-2 break-all">
                            {p.title || p.url || p.id}
                          </td>
                          <td className="px-3 py-2 text-zinc-400">{fmt(p.created_at)}</td>
                          <td className="px-3 py-2 text-right">
                            {p.thumbnail_url ? (
                              <a
                                href={p.thumbnail_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-zinc-700/60 hover:border-pink-600/60 hover:text-pink-300"
                              >
                                Open
                              </a>
                            ) : (
                              <span className="text-zinc-500">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pager page={projectsPage} pageSize={PAGE_SIZE} total={projectsTotal} onPage={setProjectsPage} />
              </>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
